import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { createItem, updateItem, ItemRequestStatus } from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import ProductBrand, { newProductBrand } from "../../models/product-brand";

interface ProductBrandFormProps {
  initialProductBrand?: ProductBrand;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const ProductBrandForm: React.FC<ProductBrandFormProps> = ({
  initialProductBrand,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingProductBrand, setEditingProductBrand] = useState<ProductBrand>(
    initialProductBrand ? initialProductBrand : newProductBrand()
  );

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  // El código solo se congela una vez que hay productos usándolo: forma parte
  // de los códigos de barra ya impresos.
  const codeLocked =
    editingProductBrand.id !== undefined &&
    (editingProductBrand.productsCount === undefined ||
      editingProductBrand.productsCount > 0);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProductBrand({
      ...editingProductBrand,
      name: e.target.value,
    });
  };

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProductBrand({
      ...editingProductBrand,
      code: e.target.value.toUpperCase(),
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendProductBrand = {
      ...editingProductBrand,
      companyId: company.id,
    };
    let requestPromise: Promise<ItemRequestStatus<ProductBrand>>;
    if (editingProductBrand.id === undefined) {
      requestPromise = createItem<ProductBrand>(
        "/product_brands/create/",
        toSendProductBrand
      );
    } else {
      requestPromise = updateItem<ProductBrand>(
        `/product_brands/${toSendProductBrand.id}/`,
        toSendProductBrand
      );
    }

    const brandStatus = await requestPromise;

    if (brandStatus.status !== SUCCESS) {
      if (brandStatus.errors !== undefined) {
        setErrors(brandStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (brandStatus.detail !== undefined) {
        message = brandStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingProductBrand(newProductBrand());
  };

  useEffect(() => {
    setEditingProductBrand(
      initialProductBrand ? initialProductBrand : newProductBrand()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProductBrand]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingProductBrand.name)}
                onChange={onNameChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Código:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                maxLength={10}
                disabled={codeLocked}
                value={emptyValueOnUndefined(editingProductBrand.code)}
                onChange={onCodeChange}
                placeholder="Ej: LU"
              ></CInput>
              <small className="text-muted">
                {codeLocked
                  ? "El código no se puede modificar: la marca ya tiene productos y forma parte de los códigos de barra impresos."
                  : "Prefijo que se agrega a los códigos de barra alfanuméricos de esta marca. Solo letras y números."}
              </small>
              <FieldErrors
                errors={getFieldErrors("code", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default ProductBrandForm;
